
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { post } from '@/services/http';
import { CurrencyHelper } from '@/helpers/CurrencyHelper';
import { EventBus } from '@/main';

@Component
export default class PORecordPaymentDialog extends Vue {
  @Prop({ type: Boolean, default: false }) readonly value!: boolean;
  @Prop({ type: Number, required: true }) readonly purchaseOrderId!: number;
  @Prop({ type: String, default: 'USD' }) readonly currencyCode!: string;
  @Prop({ type: Boolean, default: false }) readonly isForeignCurrency!: boolean;
  @Prop({ type: Number, default: 0 }) readonly outstandingAmount!: number;

  private show = false;
  private saving = false;
  private errorMessage = '';
  private dateMenu = false;

  private amount: number | null = null;
  private paymentDate = '';
  private paymentMethod: string | null = null;
  private referenceNumber = '';
  private notes = '';

  private readonly methodOptions = [
    { text: 'Bank transfer', value: 'bank_transfer' },
    { text: 'Credit card', value: 'credit_card' },
    { text: 'Check', value: 'check' },
    { text: 'Cash', value: 'cash' },
    { text: 'ACH', value: 'ach' },
    { text: 'Wire', value: 'wire' },
    { text: 'PayPal', value: 'paypal' },
    { text: 'Other', value: 'other' },
  ];

  @Watch('value', { immediate: true })
  onValueChange(val: boolean): void {
    this.show = val;
    if (val) this.reset();
  }

  @Watch('show')
  onShowChange(val: boolean): void {
    this.$emit('input', val);
  }

  private reset(): void {
    this.amount = this.outstandingAmount > 0 ? this.outstandingAmount : null;
    this.paymentDate = new Date().toISOString().slice(0, 10);
    this.paymentMethod = null;
    this.referenceNumber = '';
    this.notes = '';
    this.errorMessage = '';
  }

  get currencyStep(): string {
    return CurrencyHelper.getCurrencyStep(this.currencyCode);
  }

  get currencySymbol(): string {
    return CurrencyHelper.getCurrencySymbol(this.currencyCode);
  }

  formatCurrency(value: number): string {
    return CurrencyHelper.formatCurrencyWithCode(value as any, this.currencyCode);
  }

  get amountError(): string {
    if (this.amount === null || this.amount === undefined) return '';
    const amt = Number(this.amount);
    if (amt <= 0) return 'Amount must be greater than 0';
    if (this.outstandingAmount > 0 && amt > this.outstandingAmount) {
      return `Amount cannot exceed the remaining balance (${this.formatCurrency(this.outstandingAmount)})`;
    }
    return '';
  }

  get isValid(): boolean {
    const amt = Number(this.amount);
    return amt > 0
      && (this.outstandingAmount <= 0 || amt <= this.outstandingAmount)
      && !!this.paymentDate;
  }

  private close(): void {
    this.show = false;
  }

  private async save(): Promise<void> {
    if (!this.isValid) return;
    this.saving = true;
    this.errorMessage = '';
    try {
      const res: any = await post(`/purchase_orders/${this.purchaseOrderId}/payments`, {
        amount: Number(this.amount),
        paymentDate: this.paymentDate,
        paymentMethod: this.paymentMethod || undefined,
        referenceNumber: this.referenceNumber || undefined,
        notes: this.notes || undefined,
      });

      EventBus.$emit('show-snackbar', { message: 'Payment recorded successfully', color: 'success' });
      this.$emit('saved', res?.data ?? res);
      this.close();
    } catch (error: any) {
      this.errorMessage = error?.response?.data?.message || error.message || 'Failed to record payment';
    } finally {
      this.saving = false;
    }
  }
}
